import { useEffect, useMemo, useState } from "react";

import { useHistory } from "react-router-dom";

import { useGetCurrentOwner, useGetTradingAccount } from "~/v2/api/hooks";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import type { AcatsTransferBannerData } from "~/v2/types/acats";
import { AcatsTransferManager } from "~/v2/utils/acatsTransferManager";

export const useAcatsBanners = () => {
  const history = useHistory();
  const { product, currentAccount } = useAccountContext();
  const { account: tradingAccount } = useGetTradingAccount();

  // Get current owner for ACATS transfers using the reusable hook
  const { currentOwner } = useGetCurrentOwner({
    accountId: tradingAccount?.id,
    enabled: !!tradingAccount?.id,
  });

  // Get current account ID for ACATS transfer initialization
  const currentAccountId = currentAccount?.id;

  // Fetch ACATS transfers from localStorage
  const [acatsTransfers, setAcatsTransfers] = useState<
    AcatsTransferBannerData[]
  >([]);

  useEffect(() => {
    const initializeAndFetchAcatsTransfers = async () => {
      // Only run for live accounts - ACATS transfers are not applicable to paper accounts
      if (!currentOwner?.owner_id || !currentAccountId || product !== "live") {
        setAcatsTransfers([]);
        return;
      }

      try {
        // Check if owner has transfers in localStorage, if not initialize from API
        if (!AcatsTransferManager.hasOwnerTransfers(currentOwner.owner_id)) {
          await AcatsTransferManager.initializeOwnerTransfersFromApi(
            currentOwner.owner_id,
            [currentAccountId],
          );
        }

        // Fetch transfers for display for the current account only
        const transfers =
          await AcatsTransferManager.getAccountTransfersForDisplay(
            currentOwner.owner_id,
            currentAccountId,
          );
        setAcatsTransfers(transfers);
      } catch (error) {
        console.error("Failed to fetch ACATS transfers:", error);
        setAcatsTransfers([]);
      }
    };

    initializeAndFetchAcatsTransfers();
  }, [currentOwner?.owner_id, currentAccountId, product]);

  // Map ACATS transfers to PaginatedBanner format
  return useMemo(
    () =>
      acatsTransfers.map((transfer) => ({
        title: transfer.title,
        description: transfer.description,
        variant:
          transfer.variant === "green"
            ? ("success" as const)
            : transfer.variant === "blue"
              ? ("information" as const)
              : ("warning" as const), // red -> warning
        onViewDetails: () => {
          if (currentOwner?.owner_id && currentAccountId) {
            // Dismiss all transfers for this account before redirecting
            AcatsTransferManager.dismissAccountTransfers(
              currentOwner.owner_id,
              currentAccountId,
            );
          }

          history.push({
            pathname: Path.ROUTE_FUNDING_HISTORY,
            search: "?tab=portfolio-transfers",
          });
        },
        onDismiss: () => {
          if (!currentOwner?.owner_id || !currentAccountId) {
            return;
          }

          // Dismiss all transfers for this account
          AcatsTransferManager.dismissAccountTransfers(
            currentOwner.owner_id,
            currentAccountId,
          );

          // Remove dismissed transfers from the state to update UI immediately
          setAcatsTransfers([]);
        },
      })),
    [acatsTransfers, history, currentOwner?.owner_id, currentAccountId],
  );
};
